import { Helmet } from "react-helmet";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../features/auth/authSlice";
import {
  BOARD_LABEL,
  BOARD_SHORT,
  BOARDS,
  LeaderboardBoard,
  PeriodType,
  useGetBoardQuery,
  useGetMyRankingQuery,
  useGetPeriodsQuery,
} from "../../features/leaderboards/leaderboardsSlice";
import { useDisplayCurrency } from "../../components/LeaderboardReward/useDisplayCurrency";
import { countdown, dailyRewardNote, movementLabel, periodHeadline, periodLabel } from "../../lib/leaderboard/format";

type Mode = "today" | "month" | "past";
const isBoard = (v: unknown): v is LeaderboardBoard => v === "gift-card" || v === "crypto";
const medal = (rank: number) => (rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : `#${rank}`);

/** Leaderboard rewards (task-list 5.0 T11): tabs → Today / This month / Past → pool, prizes, You row. */
export const Leaderboards = () => {
  const [params] = useSearchParams();
  const user = useSelector(selectCurrentUser);
  const [board, setBoard] = useState<LeaderboardBoard>(isBoard(params.get("board")) ? (params.get("board") as LeaderboardBoard) : "gift-card");
  const [mode, setMode] = useState<Mode>(params.get("periodType") === "daily" ? "today" : "month");
  const [pastKey, setPastKey] = useState<string | undefined>();
  const { currency, cycle, money } = useDisplayCurrency();
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  // Deep links (push / winner popup) can change the query while this page is mounted.
  useEffect(() => {
    const b = params.get("board");
    const pt = params.get("periodType");
    if (isBoard(b)) setBoard(b);
    if (pt === "daily") setMode("today");
    if (pt === "monthly") setMode("month");
  }, [params]);

  const periodType: PeriodType = mode === "today" ? "daily" : "monthly";
  const { currentData: periods, isFetching: periodsFetching } = useGetPeriodsQuery({ board, periodType: "monthly", limit: 12 }, { skip: mode !== "past" });
  const selectedPast = pastKey ?? periods?.[0]?.periodKey;
  const noPast = mode === "past" && !periodsFetching && (periods?.length ?? 0) === 0;
  const args = useMemo(() => ({ board, periodType, limit: 50, ...(mode === "past" && selectedPast ? { periodKey: selectedPast } : {}) }), [board, periodType, mode, selectedPast]);
  // `currentData` is undefined until THIS selection resolves — never another board's rows under the new header.
  const { currentData: data, isFetching, isError, isUninitialized, refetch } = useGetBoardQuery(args, { skip: mode === "past" && !selectedPast });
  const { currentData: me } = useGetMyRankingQuery({ board, periodType, userId: user?.id }, { skip: !user || mode === "past" });
  const showPrizes = !!data?.programmeEnabled;

  return (
    <div className="font-dashboard pt-8 pb-12 lg:px-4">
      <Helmet>
        <title>Leaderboards</title>
        <meta name="description" content="Leaderboards"></meta>
        <link rel="canonical" href="https://gcbuying.com/app/leaderboards" />
      </Helmet>

      <div className="container-lg grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3 min-h-screen">
          <div className="bg-black flex flex-col px-4 lg:px-10 py-6 rounded-lg">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-white text-2xl font-medium">Leaderboard</h1>
              {user && <Link to="/app/leaderboards/rewards" className="text-primary text-sm font-medium hover:underline">My rewards →</Link>}
            </div>

            <div className="flex flex-wrap gap-3 mb-6">
              <div className="flex bg-gray-700 rounded-lg p-1">
                {BOARDS.map((b) => (
                  <button key={b} className={`py-2 px-4 rounded-md text-sm ${board === b ? "bg-primary text-black" : "text-white/80"}`} onClick={() => { setBoard(b); setPastKey(undefined); }}>
                    {BOARD_SHORT[b]}
                  </button>
                ))}
              </div>
              <div className="flex bg-gray-700 rounded-lg p-1">
                {(["today", "month", "past"] as Mode[]).map((m) => (
                  <button key={m} className={`py-2 px-4 rounded-md text-sm ${mode === m ? "bg-white text-black" : "text-white/80"}`} onClick={() => { setMode(m); setPastKey(undefined); }}>
                    {m === "today" ? "Today" : m === "month" ? "This month" : "Past"}
                  </button>
                ))}
              </div>
            </div>

            {mode === "past" && (
              <div className="flex flex-wrap gap-2 mb-5">
                {periodsFetching && !periods && <p className="text-white/50 text-sm">Loading settled months…</p>}
                {(periods ?? []).map((p) => (
                  <button key={p.periodKey} onClick={() => setPastKey(p.periodKey)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${selectedPast === p.periodKey ? "bg-white text-black border-white" : "text-white/80 border-white/20"}`}>
                    {periodLabel("monthly", p.periodKey)}{p.winnerDisplayName ? ` · 🥇 ${p.winnerDisplayName}` : ""}
                  </button>
                ))}
              </div>
            )}

            {noPast ? (
              <p className="text-white/50 text-center py-16">No settled months yet. The first month closes at month end; its final board and prizes will appear here.</p>
            ) : isFetching && !data ? (
              <div className="flex justify-center items-center py-20">
                <div className="h-10 w-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : isError && !data ? (
              <div className="flex flex-col items-center py-20">
                <p className="text-red-500">Failed to load leaderboard data</p>
                <button className="mt-4 bg-primary py-2 px-6 rounded-lg text-black font-medium" onClick={() => { if (!isUninitialized) refetch(); }}>Try again</button>
              </div>
            ) : !data ? null : (
              <>
                {showPrizes ? (
                  <div className="rounded-2xl bg-primary/95 text-black p-5 mb-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold tracking-widest opacity-80">🏆 {periodHeadline(data.periodType, data.periodKey)} {BOARD_LABEL[data.board].toUpperCase()}</p>
                        <p className="text-3xl font-extrabold mt-1">{money(data.poolTotal)}</p>
                        <p className="text-sm font-semibold">PRIZE POOL</p>
                      </div>
                      <button onClick={cycle} className="rounded-full bg-black/10 px-3 py-1.5 text-xs font-bold">{currency.toUpperCase()} ⇄</button>
                    </div>
                    <p className="text-sm mt-3">Finish in the Top 10 to win your share.</p>
                    {data.periodType === "daily" && data.prizes.length > 0 && <p className="text-xs mt-2 opacity-80">{dailyRewardNote(data.prizes, money)}</p>}
                    <p className="text-xs mt-3 opacity-80">{data.locked ? `🔒 Settled · ${data.participants} ranked` : `⏳ Settles in ${countdown(data.settlesAt)} · ${data.participants} ranked`}</p>
                  </div>
                ) : (
                  <div className="rounded-2xl bg-gray-800 text-white/80 p-4 mb-5 text-sm">
                    <span className="font-semibold text-white">{BOARD_LABEL[data.board]}</span> · {periodHeadline(data.periodType, data.periodKey)} · rankings refresh every 5 minutes.
                  </div>
                )}

                {data.rows.length === 0 ? (
                  <p className="text-white/50 text-center py-16">No one ranked yet — complete a trade in this period to appear here.</p>
                ) : (
                  <div className="space-y-2">
                    {data.rows.map((r) => (
                      <div key={r.rank} className={`flex items-center rounded-xl px-4 py-3 ${r.isYou ? "bg-primary/20 border border-primary" : "bg-gray-800"}`}>
                        <div className="w-10 text-center text-white font-bold">{medal(r.rank)}</div>
                        <div className="flex-1 ml-3">
                          <p className="text-white font-medium">{r.displayName}{r.isYou ? " (you)" : ""}</p>
                          <p className={`text-xs ${r.movement && r.movement > 0 ? "text-green-400" : r.movement && r.movement < 0 ? "text-red-400" : "text-white/40"}`}>
                            {movementLabel(r.movement)}{!r.qualified ? " · below minimum" : ""}
                          </p>
                        </div>
                        {showPrizes && <div className={`font-bold ${r.prize > 0 ? "text-white" : "text-white/30"}`}>{r.prize > 0 ? money(r.prize) : "—"}</div>}
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-white/40 text-xs text-center mt-4">
                  {data.locked ? "Final positions for this period." : "Positions refresh every 5 minutes. Trades completed after settlement count toward the next period."}
                </p>
              </>
            )}

            {user && mode !== "past" && (
              <div className="mt-6 rounded-xl bg-gray-900 border border-white/10 px-4 py-3 text-sm text-white">
                {!me || me.rank == null
                  ? "Not ranked yet — trade to appear on the board."
                  : [
                      `You're #${me.rank}`,
                      me.movement ? movementLabel(me.movement) : null,
                      me.gapToAbove != null ? `${money(me.gapToAbove)} behind #${me.rank - 1}` : null,
                      me.rank > 10 && me.gapToTop10 != null ? `${money(me.gapToTop10)} to Top 10` : null,
                      showPrizes && me.prize > 0 ? `prize at this position ${money(me.prize)}` : null,
                      !me.qualified ? "below minimum volume" : null,
                    ].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
        </div>
        <div className="hidden lg:block">
          <div className="bg-black rounded-lg p-4 text-white/70 text-sm space-y-3">
            <p className="text-white font-medium">How it works</p>
            <p>Daily prizes go to the top positions when each day closes (Lagos time). The monthly prize pool is split across the Top 10 at month end.</p>
            <p>Prizes are credited to your Reward balance automatically. Names are masked for privacy.</p>
            <p>Rankings are updated every 5 minutes.</p>
          </div>
        </div>
      </div>
    </div>
  );
};
